import { useState } from 'react';
import { isStudentEmail, loginEmail, normaliseUsername, USERNAME_RE } from '../lib/accounts';
import { studentSignup } from '../lib/api';
import { supabase } from '../lib/supabase';

// Three doors.
//
//   Sign in      a username (students) or an email (teachers), and a password.
//   New student  name, username, password and the class code from the board.
//   New teacher  name, email and password.
//
// These accounts belong to the CLASSROOM's own Supabase project. A Gibberfish
// app account does not work here and a classroom account does not work in the
// app — they are separate products. Forgot-password only works for teachers; a
// student's password is reset by their teacher from the roster, since there is
// no inbox to send to.
type Mode = 'signin' | 'student' | 'teacher';

export default function Login() {
  const [mode, setMode] = useState<Mode>(() =>
    new URLSearchParams(window.location.search).get('code') ? 'student' : 'signin');

  return (
    <div className="auth">
      <div className="panel">
        <h1>🐠 Gibberfish</h1>
        <p className="sub">Classroom</p>

        <div className="seg" role="tablist">
          {([['signin', 'Sign in'], ['student', 'New student'], ['teacher', 'New teacher']] as const).map(([m, label]) => (
            <button key={m} role="tab" aria-selected={mode === m} className={mode === m ? 'on' : ''} onClick={() => setMode(m)}>
              {label}
            </button>
          ))}
        </div>

        {mode === 'signin' ? <SignIn /> : mode === 'student' ? <SignUp /> : <TeacherSignUp />}

        <p className="small muted" style={{ marginTop: 18, marginBottom: 0, textAlign: 'center' }}>
          Classroom accounts are separate from the Gibberfish app.
        </p>
      </div>
      <About />
    </div>
  );
}

/** What the site is, for anyone who arrives signed out (and Google's reviewers). */
function About() {
  return (
    <section className="auth-about">
      <h2>Language homework that gets done</h2>
      <p>
        Gibberfish Classroom is a homework website for language teachers. Teachers assign a weekly video, a
        story and a grammar lesson; students watch with subtitles they can tap to translate, read with a check
        that they really read, practise grammar, and review the words they saved as flashcards. Teachers see
        who did what, and for how long.
      </p>
      <ul>
        <li><strong>Videos</strong> with target-language subtitles and tap-to-translate.</li>
        <li><strong>Stories</strong> from a built-in graded library or your own, timed to a reading pace you set.</li>
        <li><strong>Grammar lessons</strong> and a live class quiz game.</li>
        <li><strong>Flashcards</strong> that schedule each student’s review.</li>
      </ul>
      <h3>Google Classroom</h3>
      <p>
        Teachers can import a class list from Google Classroom instead of typing it. With the teacher’s
        permission, the site reads their class names and their students’ names (read-only, never emails or
        grades) and uses them only to create student accounts in that teacher’s class. Nothing else from Google
        is stored. Details are in the <a href="/privacy">privacy policy</a>.
      </p>
      <p className="small muted">
        30-day free trial for teachers, then $10 a month or $90 a year. ·{' '}
        <a href="/privacy">Privacy policy</a> · <a href="/terms">Terms of service</a> ·{' '}
        <a href="mailto:josiah29reed@gmail.com">Contact</a>
      </p>
    </section>
  );
}

function SignIn() {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const isEmail = identifier.includes('@');

  const signIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null); setNotice(null);
    const { error: err } = await supabase.auth.signInWithPassword({
      email: loginEmail(identifier), password,
    });
    if (err) {
      setError(/invalid login/i.test(err.message)
        ? (isEmail ? 'Wrong email or password.' : 'Wrong username or password.')
        : err.message);
    }
    setBusy(false);
  };

  const forgot = async () => {
    if (!isEmail) {
      setNotice('Students: ask your teacher to reset your password. Teachers: type your email first.');
      return;
    }
    setBusy(true); setError(null);
    const { error: err } = await supabase.auth.resetPasswordForEmail(identifier.trim(), {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (err) setError(err.message); else setNotice('Check your email for a reset link.');
    setBusy(false);
  };

  return (
    <form onSubmit={signIn}>
      <div className="field">
        <label htmlFor="identifier">Username (or teacher email)</label>
        <input
          id="identifier" autoComplete="username" required autoCapitalize="none" spellCheck={false}
          value={identifier} onChange={e => setIdentifier(e.target.value)}
        />
      </div>
      <div className="field">
        <label htmlFor="password">Password</label>
        <input
          id="password" type="password" autoComplete="current-password" required
          value={password} onChange={e => setPassword(e.target.value)}
        />
      </div>

      {error && <div className="notice bad small" style={{ marginBottom: 14 }}>{error}</div>}
      {notice && <div className="notice small" style={{ marginBottom: 14 }}>{notice}</div>}

      <button className="primary" type="submit" disabled={busy} style={{ width: '100%' }}>
        {busy ? 'Signing in…' : 'Sign in'}
      </button>

      <div className="row" style={{ marginTop: 16, justifyContent: 'center' }}>
        <button type="button" className="small" onClick={forgot} disabled={busy}>Forgot password</button>
      </div>
    </form>
  );
}

function SignUp() {
  const [displayName, setDisplayName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [joinCode, setJoinCode] = useState(
    () => (new URLSearchParams(window.location.search).get('code') ?? '').toUpperCase(),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const u = normaliseUsername(username);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!USERNAME_RE.test(u)) {
      setError('Usernames are 3–24 characters: letters, numbers, dots and underscores. No spaces.');
      return;
    }
    if (password.length < 6) { setError('Use at least 6 characters for your password.'); return; }
    if (password !== confirm) { setError('The two passwords do not match.'); return; }

    setBusy(true);
    try {
      await studentSignup({
        username: u, password, displayName: displayName.trim(), joinCode: joinCode.trim().toUpperCase(),
      });
      const { error: err } = await supabase.auth.signInWithPassword({ email: loginEmail(u), password });
      if (err) throw new Error(`Your account was made, but signing in failed: ${err.message}`);
      window.history.replaceState(null, '', '/');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <div className="field">
        <label htmlFor="code">Class code (from your teacher)</label>
        <input
          id="code" required autoCapitalize="characters" spellCheck={false} maxLength={6}
          className="code-input"
          value={joinCode} onChange={e => setJoinCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
        />
      </div>
      <div className="field">
        <label htmlFor="name">Your name (so your teacher knows it's you)</label>
        <input
          id="name" required maxLength={60} autoComplete="name" placeholder="Maria G."
          value={displayName} onChange={e => setDisplayName(e.target.value)}
        />
      </div>
      <div className="field">
        <label htmlFor="new-username">Pick a username</label>
        <input
          id="new-username" required autoCapitalize="none" spellCheck={false} autoComplete="username"
          maxLength={24}
          value={username} onChange={e => setUsername(e.target.value.replace(/\s/g, ''))}
        />
        <span className="hint">You'll use this to sign in. No email needed.</span>
      </div>
      <div className="field">
        <label htmlFor="new-password">Pick a password</label>
        <input
          id="new-password" type="password" required autoComplete="new-password"
          value={password} onChange={e => setPassword(e.target.value)}
        />
      </div>
      <div className="field">
        <label htmlFor="confirm-password">Type it again</label>
        <input
          id="confirm-password" type="password" required autoComplete="new-password"
          value={confirm} onChange={e => setConfirm(e.target.value)}
        />
      </div>

      {error && <div className="notice bad small" style={{ marginBottom: 14 }}>{error}</div>}

      <button className="primary" type="submit" disabled={busy} style={{ width: '100%' }}>
        {busy ? 'Creating your account…' : 'Create account'}
      </button>
    </form>
  );
}

function TeacherSignUp() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const addr = email.trim();
    if (isStudentEmail(addr)) { setError('Use your own email address.'); return; }
    if (password.length < 6) { setError('Use at least 6 characters for your password.'); return; }
    if (password !== confirm) { setError('The two passwords do not match.'); return; }

    setBusy(true);
    const { data, error: err } = await supabase.auth.signUp({
      email: addr,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: { display_name: name.trim(), role: 'teacher' },
      },
    });
    setBusy(false);
    if (err) {
      setError(/already registered|already exists/i.test(err.message)
        ? 'There is already a classroom account with that email. Sign in instead.'
        : err.message);
      return;
    }
    // With email confirmation off, signUp returns a session and the Gate takes
    // over. With it on, the account waits for the link in the inbox.
    if (!data.session) setSentTo(addr);
  };

  if (sentTo) {
    return (
      <div className="notice good">
        Almost done. We sent a confirmation link to <strong>{sentTo}</strong>. Open it, then sign in.
      </div>
    );
  }

  return (
    <form onSubmit={submit}>
      <div className="field">
        <label htmlFor="t-name">Your name (students see this)</label>
        <input id="t-name" required maxLength={60} autoComplete="name" placeholder="Sr. Reed"
          value={name} onChange={e => setName(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="t-email">Email</label>
        <input id="t-email" type="email" required autoComplete="email"
          value={email} onChange={e => setEmail(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="t-password">Password</label>
        <input id="t-password" type="password" required autoComplete="new-password"
          value={password} onChange={e => setPassword(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="t-confirm">Type it again</label>
        <input id="t-confirm" type="password" required autoComplete="new-password"
          value={confirm} onChange={e => setConfirm(e.target.value)} />
      </div>

      {error && <div className="notice bad small" style={{ marginBottom: 14 }}>{error}</div>}

      <button className="primary" type="submit" disabled={busy} style={{ width: '100%' }}>
        {busy ? 'Creating your account…' : 'Create teacher account'}
      </button>
    </form>
  );
}
